<template>
  <div class="px-5 py-4">
    <integration-page-header
      :title="$t('INTEGRATION_SETTINGS.LIST.TITLE')"
      :show-breadcrumb="false"
      :hint-title="$t('INTEGRATION_SETTINGS.LIST.HINT.TITLE')"
      :hint-description="$t('INTEGRATION_SETTINGS.LIST.HINT.DESC')"
    >
      <template #hint>
        <div class="text-sm leading-5 font-semibold text-gray-400 mb-[11px]">
          {{ $t('INTEGRATION_SETTINGS.LIST.HINT.HOW_TITLE') }}
        </div>
        <ul
          class="list-disc ml-0 ltr:pl-5 rtl:pr-5 mb-0 flex flex-col gap-2 text-sm leading-5 text-gray-800"
        >
          <li
            v-for="(item, index) in hintHowItems"
            :key="index"
            v-dompurify-html="item"
          />
        </ul>
      </template>
    </integration-page-header>

    <input-v2
      v-model="search"
      icon="search"
      class="mt-3 max-w-[584px]"
      :placeholder="$t('INTEGRATION_SETTINGS.LIST.SEARCH_PLACEHOLDER')"
    />

    <div class="mt-3 h-12 flex flex-row items-center justify-between">
      <div class="flex flex-row items-center">
        <span class="text-lg leading-[26px] text-[#000] ltr:mr-3 rtl:ml-3">
          {{ $t('INTEGRATION_SETTINGS.LIST.CATEGORY_LABEL') }}
        </span>
        <category-filter v-model="category" />
      </div>
      <view-toggle v-model="view" />
    </div>

    <woot-loading-state v-if="uiFlags.isFetching" class="mt-8" />
    <empty-state
      v-else-if="!filteredList.length"
      :title="$t('INTEGRATION_SETTINGS.LIST.NO_RESULTS')"
    />
    <div
      v-else-if="view === 'grid'"
      class="mt-8 grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
    >
      <integration-card
        v-for="item in filteredList"
        :key="item.id"
        :integration="item"
        variant="grid"
        :category="categoryOf(item)"
        :enabled="enabledOf(item)"
        :show-toggle="showToggleOf(item)"
        :toggle-disabled="toggleDisabledOf(item)"
        :manage-disabled="manageDisabledOf(item)"
        @manage="onManage(item)"
        @toggle="onToggle(item, $event)"
      />
    </div>
    <div v-else class="mt-6 flex flex-col gap-3">
      <integration-card
        v-for="item in filteredList"
        :key="item.id"
        :integration="item"
        variant="row"
        :category="categoryOf(item)"
        :enabled="enabledOf(item)"
        :show-toggle="showToggleOf(item)"
        :toggle-disabled="toggleDisabledOf(item)"
        :manage-disabled="manageDisabledOf(item)"
        @manage="onManage(item)"
        @toggle="onToggle(item, $event)"
      />
    </div>

    <disable-integration-modal
      v-if="disableTarget"
      :show="!!disableTarget"
      :integration-name="disableTarget.name"
      :is-loading="busy"
      :on-close="closeDisableModal"
      :on-confirm="confirmDisable"
    />

    <chat-gpt-enable-modal
      v-if="openModal === 'openai_enable'"
      :show="openModal === 'openai_enable'"
      @close="closeManageModal"
    />

    <gpt-settings-modal
      v-if="openModal === 'openai'"
      :show="openModal === 'openai'"
      @close="closeManageModal"
    />

    <click2-call-settings-modal
      v-if="openModal === 'click2call'"
      :show="openModal === 'click2call'"
      :has-hook="click2CallHasHook"
      @close="closeManageModal"
      @saved="closeManageModal"
    />

    <doc-signature-enable-modal
      v-if="openModal === 'doc_signature'"
      :show="openModal === 'doc_signature'"
      @close="closeManageModal"
    />

    <forms-enable-modal
      v-if="openModal === 'forms_enable'"
      :show="openModal === 'forms_enable'"
      @close="closeManageModal"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import ChatGptEnableModal from 'dashboard/components/widgets/digital99Assistance/ChatGptEnableModal.vue';
import DocSignatureEnableModal from 'dashboard/components/widgets/docSignature/DocSignatureEnableModal.vue';
import FormsEnableModal from 'dashboard/components/widgets/forms/FormsEnableModal.vue';
import Click2CallSettingsModal from '../Click2Call/Click2CallSettingsModal.vue';
import GptSettingsModal from './ChatGpt/GptSettingsModal.vue';
import CategoryFilter from './components/CategoryFilter.vue';
import DisableIntegrationModal from './components/DisableIntegrationModal.vue';
import IntegrationCard from './components/IntegrationCard.vue';
import IntegrationPageHeader from './components/IntegrationPageHeader.vue';
import ViewToggle from './components/ViewToggle.vue';
import integrationActionsMixin from './integrationActionsMixin';
import {
  INTEGRATION_META,
  INTEGRATION_PERMISSION_NAMES,
  filterIntegrations,
  orderIntegrations,
} from './constants';

export default {
  components: {
    CategoryFilter,
    ChatGptEnableModal,
    Click2CallSettingsModal,
    DisableIntegrationModal,
    DocSignatureEnableModal,
    EmptyState,
    FormsEnableModal,
    GptSettingsModal,
    IntegrationCard,
    IntegrationPageHeader,
    ViewToggle,
  },
  mixins: [hasPermissionMixin, uiSettingsMixin, integrationActionsMixin],
  data() {
    return {
      search: '',
      category: 'all',
      view: this.$store.getters.getUISettings.integrations_view || 'grid',
      openModal: null,
      disableTarget: null,
      busy: false,
    };
  },
  computed: {
    ...mapGetters({
      integrationsList: 'integrations/getIntegrations',
      appIntegrationsList: 'integrations/getAppIntegrations',
      uiFlags: 'integrations/getUIFlags',
    }),
    hintHowItems() {
      return [
        this.$t('INTEGRATION_SETTINGS.LIST.HINT.HOW_1'),
        this.$t('INTEGRATION_SETTINGS.LIST.HINT.HOW_2'),
        this.$t('INTEGRATION_SETTINGS.LIST.HINT.HOW_3'),
      ];
    },
    list() {
      const list = [
        ...this.integrationsList,
        ...this.appIntegrationsList.map(x => ({ ...x, isApp: true })),
      ]
        .filter(x => x.visible !== false)
        .filter(item => this.hasReadPermission(this.permissionName(item)))
        .map(item => ({ ...item, description: this.descriptionOf(item) }));
      return orderIntegrations(list);
    },
    filteredList() {
      return filterIntegrations(this.list, {
        search: this.search,
        category: this.category,
      });
    },
    click2CallHasHook() {
      const item = this.list.find(x => x.id === 'click_to_call');
      return (item?.hooks || []).length > 0;
    },
  },
  watch: {
    view(value) {
      this.updateUISettings({ integrations_view: value });
    },
  },
  mounted() {
    this.$store.dispatch('integrations/get');
  },
  methods: {
    permissionName(integration) {
      return (
        INTEGRATION_PERMISSION_NAMES[integration.id] ||
        integration.id
          .replace(/_/g, ' ')
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      );
    },
    descriptionOf(item) {
      const key = `INTEGRATION_SETTINGS.LIST.DESCRIPTIONS.${item.id.toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : item.description;
    },
    categoryOf(item) {
      return INTEGRATION_META[item.id]?.category || '';
    },
    enabledOf(item) {
      return item.isApp ? (item.hooks || []).length > 0 : item.enabled;
    },
    showToggleOf(item) {
      return !!INTEGRATION_META[item.id]?.hasToggle;
    },
    toggleDisabledOf(item) {
      if (
        this.busy ||
        this.uiFlags.isCreatingHook ||
        this.uiFlags.isDeletingHook
      ) {
        return true;
      }
      const permission = this.permissionName(item);
      return this.enabledOf(item)
        ? !this.hasDeletePermission(permission)
        : !this.hasCreatePermission(permission);
    },
    manageDisabledOf(item) {
      const meta = INTEGRATION_META[item.id];
      return !meta?.manage || (!!meta.manageNeedsHook && !this.enabledOf(item));
    },
    onManage(item) {
      const { manage } = INTEGRATION_META[item.id] || {};
      if (!manage) return;
      if (manage.type === 'route') {
        this.$router.push({ name: manage.name });
        return;
      }
      this.openModal = manage.key;
    },
    async onToggle(item, value) {
      if (!value) {
        this.disableTarget = item;
        return;
      }
      if (item.id === 'openai' || item.id === 'forms') {
        this.openModal = `${item.id}_enable`;
        return;
      }
      if (this.busy) return;
      this.busy = true;
      await this.addApp(item);
      this.busy = false;
    },
    closeDisableModal() {
      this.disableTarget = null;
    },
    async confirmDisable() {
      if (this.busy) return;
      this.busy = true;
      await this.deleteApp(this.disableTarget);
      this.busy = false;
      this.disableTarget = null;
    },
    closeManageModal() {
      this.openModal = null;
    },
  },
};
</script>
